import { publicSignInDeps, publicSignInInput, useSignInQuery } from "../lib/published-auth";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import authStyles from "../styles/auth.css?url";
import membershipStyles from "../styles/membership.css?url";
import { SignInForm, SignInPage } from "../components/auth/sign-in-page";
import { DocumentContent } from "../components/document-content";
import { useShell } from "../lib/document-route";
import { signInDeps } from "../lib/auth-route";
import { creatorPageHead, shellFrom, withStylesheet } from "../lib/head";
import { keepSearch } from "../lib/search";
import { loadSignIn } from "../server/auth";

/**
 * Signing in to this creator's site: the page they composed around the form, or the native
 * page. Never indexed.
 */
export const Route = createFileRoute("/sign-in")({
  validateSearch: keepSearch,
  loaderDeps: ({ search }) => publicSignInDeps(signInDeps(search)),
  loader: async ({ deps }) => {
    const data = await loadSignIn({ data: publicSignInInput({ ...deps, signUp: false }) });
    if (data.kind === "missing") throw notFound();
    if (data.kind === "redirect") throw redirect({ href: data.to, statusCode: 302 });
    return data;
  },
  head: ({ matches, loaderData }) => {
    const shell = shellFrom(matches);
    if (!shell || !loaderData) return {};
    const head = withStylesheet(
      loaderData.kind === "page"
        ? creatorPageHead(shell, {
            pathname: "/sign-in",
            title: loaderData.page.seoTitle || loaderData.page.title,
            description: loaderData.page.description,
            noindex: true,
          })
        : creatorPageHead(shell, {
            pathname: "/sign-in",
            title: `Sign in | ${loaderData.creatorName}`,
            description: `Sign in to ${loaderData.creatorName}.`,
            noindex: true,
          }),
      authStyles,
    );
    // The plans under the form are Membership's cards.
    return loaderData.kind !== "page" && loaderData.plans.length
      ? withStylesheet(head, membershipStyles)
      : head;
  },
  component: SignIn,
});

function SignIn() {
  const data = Route.useLoaderData();
  const shell = useShell();
  const view = useSignInQuery(data.kind === "page" ? data.builtIn : data);
  if (data.kind === "page")
    return <DocumentContent data={data} shell={shell} builtIn={<SignInForm view={view} />} />;
  return <SignInPage view={view} />;
}
